import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  SwipeableDrawer,
  Box,
  Typography,
  IconButton,
  Button,
  OutlinedInput,
  InputAdornment,
  TextField,
  Skeleton,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Modal,
} from "@mui/material";
import {
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  CameraswitchOutlined,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import PhotoCaptureModal from "./PhotoCaptureModal";
import PhotoGuidelinesModal from "./PhotoGuidelinesModal";
import NotesSection from "./NotesSection";
import EntryPointNamingModal from "./EntryPointNamingModal";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import Icons from "@/assets/index";
import { Option, NoteSectionItem } from "@/types/common";
import { EntryPointType } from "./ContributeNotes";
import RequiredErrorBadge from "@/shared/components/RequiredErrorBadge";

interface EntryPointDetailsFormProps {
  open: boolean;
  onClose: () => void;
  buildingName: string;
  entryPoint: EntryPointType | null;
  onSave: (updatedEntryPoint: EntryPointType) => void;
  isExit?: boolean;
  hasError?: boolean;
  availableEntrances?: EntryPointType[];
  isEditingDraft?: boolean;
}

const photoCaptions = ["Front View", "Side View", "Wide View"];

const styles = {
  fixedHeaderContainer: {
    padding: "24px 24px 16px 24px",
    borderBottom: "1px solid #E0E0E0",
    flexShrink: 0,
    borderTopLeftRadius: "24px",
    borderTopRightRadius: "24px",
  },
  drawerContent: {
    padding: "16px 24px 80px 24px",
    flexGrow: 1,
    overflowY: "auto" as const,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: "4.71vw",
    fontWeight: 700,
    color: "#0F172A",
  },
  sectionTitle: {
    fontSize: "4.24vw",
    fontWeight: 600,
    color: "#191919",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "12px",
  },
  infoIcon: {
    color: "#666",
    fontSize: "4.71vw",
  },
  nameInput: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    marginBottom: "24px",
    "& fieldset": { borderColor: "#E0E0E0" },
  },
  editBtn: {
    color: "#9443DF",
    fontWeight: 600,
    textTransform: "none" as const,
  },
  photoGrid: {
    display: "flex",
    flexWrap: "nowrap",
    gap: "20px",
    marginBottom: "12px",
    overflowX: "auto",
    paddingTop: "12px",
    paddingBottom: "8px",
  },
  photoContainer: {
    position: "relative" as const,
    width: "80px",
    height: "80px",
  },
  photoItem: {
    width: "80px",
    height: "80px",
    borderRadius: "8px",
    objectFit: "cover" as const,
    border: "1px solid #e0e0e0",
  },
  photoDeleteButton: {
    position: "absolute" as const,
    top: -8,
    right: -8,
    backgroundColor: "#ff3b30",
    color: "white",
    width: "20px",
    height: "20px",
    minWidth: "auto",
    padding: 0,
    "&:hover": { backgroundColor: "#d32f2f" },
  },
  photoLabel: {
    fontSize: "3vw",
    color: "#666",
    textAlign: "center" as const,
    marginTop: "4px",
    fontWeight: 500,
  },
  mandatoryBadge: {
    color: "#ff3b30",
  },
  bottomBar: {
    position: "absolute" as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    padding: "16px",
    borderTop: "1px solid #e0e0e0",
    display: "flex",
    gap: "12px",
  },
  draftButton: {
    flex: 1,
    textTransform: "none" as const,
    color: "#191919",
    fontWeight: 700,
    fontSize: "3.76vw",
    padding: "12px",
    borderRadius: "30px",
    backgroundColor: "#F0DEFF",
    "&:hover": { backgroundColor: "#e2c5ff" },
  },
  submitButton: {
    flex: 1,
    textTransform: "none" as const,
    color: "#fff",
    fontWeight: 700,
    fontSize: "3.76vw",
    padding: "12px",
    borderRadius: "30px",
    background: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
    "&:hover": { backgroundColor: "#6034d6" },
  },
};

export default function EntryPointDetailsForm({
  open,
  onClose,
  buildingName,
  entryPoint,
  onSave,
  isExit = false,
  hasError = false,
  availableEntrances = [],
  isEditingDraft = false,
}: EntryPointDetailsFormProps) {
  const { api } = useApiClient();
  const { action, contribution_id } = useParams<{
    action?: string;
    contribution_id?: string;
  }>();
  const isUpdateMode = action === "update" && !!contribution_id;
  const showUpdateButtons = isUpdateMode && !isEditingDraft;

  const [name, setName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [namingModalOpen, setNamingModalOpen] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [photos, setPhotos] = useState<(string | Blob | null)[]>([
    null,
    null,
    null,
  ]);
  const [video, setVideo] = useState<string | Blob | null>(null);
  const [notesSelection, setNotesSelection] = useState<
    Record<string, Option[]>
  >({});
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [entranceNotesData, setEntranceNotesData] = useState<NoteSectionItem[]>([]);
  const [exitNotesData, setExitNotesData] = useState<NoteSectionItem[]>([]);

  const [localHasError, setLocalHasError] = useState(false);
  const displayError = hasError || localHasError;

  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [retakeIndex, setRetakeIndex] = useState<number | null>(null);
  const [loadingNotes, setLoadingNotes] = useState(true);

  const [copyAsEntrance, setCopyAsEntrance] = useState(false);
  const [selectedEntranceId, setSelectedEntranceId] = useState<string>("");
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);

  const drawerContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open && drawerContentRef.current) {
      drawerContentRef.current.scrollTop = 0;
    }
  }, [open]);

  const handleEntranceSelect = (entranceId: string) => {
    setSelectedEntranceId(entranceId);
    const entrance = availableEntrances.find((ep) => ep.id === entranceId);
    if (entrance) {
      setPhotos(entrance.photos || [null, null, null]);
      setVideo(entrance.video || null);
      setTags(entrance.tags || []);

      let mappedNotesSelection = { ...(entrance.notesSelection || {}) };
      if (isExit && exitNotesData.length > 0 && entranceNotesData.length > 0) {
        mappedNotesSelection = {};
        Object.keys(entrance.notesSelection || {}).forEach((entranceKey) => {
          const index = entranceNotesData.findIndex((n) => n.sectionHeader === entranceKey);
          if (index !== -1 && exitNotesData[index]) {
            const exitKey = exitNotesData[index].sectionHeader;
            
            const mappedOptions = entrance.notesSelection![entranceKey].map((entranceOpt: Option) => {
              const optIndex = entranceNotesData[index].notes?.findIndex((o: Option) => o.notes_data_id === entranceOpt.notes_data_id);
              if (optIndex !== -1 && exitNotesData[index].notes?.[optIndex]) {
                return {
                  ...entranceOpt,
                  notes_data_id: exitNotesData[index].notes[optIndex].notes_data_id,
                  notes: exitNotesData[index].notes[optIndex].notes
                };
              }
              return entranceOpt;
            });
            
            mappedNotesSelection[exitKey] = mappedOptions;
          } else {
            mappedNotesSelection[entranceKey] = entrance.notesSelection![entranceKey];
          }
        });
      }
      setNotesSelection(mappedNotesSelection);
    }
  };

  useEffect(() => {
    if (entryPoint && open) {
      setName(entryPoint.name || entryPoint.type);
      setPhotos(entryPoint.photos || [null, null, null]);
      setVideo(entryPoint.video || null);
      setNotesSelection(entryPoint.notesSelection || {});
      setTags(entryPoint.tags || []);
    }
  }, [entryPoint, open]);

  useEffect(() => {
    const fetchDefaultNotes = async () => {
      setLoadingNotes(true);
      try {
        const res = await api.get(ENDPOINTS.DEFAULT_NOTES.GET_ALL, {
          is_format: true,
        });
        const data = res?.data?.data;
        if (data) {
          setEntranceNotesData(data.entranceNotes || []);
          setExitNotesData(data.exitNotes || []);
        }
      } catch (error) {
        console.error("Error fetching default notes:", error);
      } finally {
        setLoadingNotes(false);
      }
    };
    if (open) fetchDefaultNotes();
  }, [api, open]);

  useEffect(() => {
    if (!namingModalOpen && isEditingName) {
      setTimeout(() => nameInputRef.current?.focus(), 0);
    }
  }, [namingModalOpen, isEditingName]);

  const handleEditClick = () => {
    if (!isEditingName) {
      const hideGuidelines =
        localStorage.getItem("hideEntryPointNamingGuidelines") === "true";
      if (!hideGuidelines) {
        setNamingModalOpen(true);
      } else {
        setTimeout(() => nameInputRef.current?.focus(), 0);
      }
    }
    setIsEditingName(!isEditingName);
  };

  const handleDeletePhoto = (index: number) => {
    const newPhotos = [...photos];
    newPhotos[index] = null;
    setPhotos(newPhotos);
    setCopyAsEntrance(false);
  };

  const handlePhotosTaken = (
    newPhotos?: string[],
    capturedVideo?: string | Blob,
  ) => {
    if (capturedVideo) {
      setVideo(capturedVideo);
      setCopyAsEntrance(false);
    } else if (newPhotos) {
      const updatedPhotos = [...photos];
      if (retakeIndex !== null) {
        updatedPhotos[retakeIndex] = newPhotos[0];
      } else {
        newPhotos.forEach((photo) => {
          const firstEmptyIndex = updatedPhotos.indexOf(null);
          if (firstEmptyIndex !== -1) {
            updatedPhotos[firstEmptyIndex] = photo;
          }
        });
      }
      setPhotos(updatedPhotos);
      setCopyAsEntrance(false);
    }
    setPhotoModalOpen(false);
    setRetakeIndex(null);
  };

  const handleTagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTag(e.target.value);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (newTag.trim() && !tags.includes(newTag.trim())) {
        setTags([...tags, newTag.trim()]);
        setCopyAsEntrance(false);
      }
      setNewTag("");
    } else if (e.key === "Backspace" && newTag === "" && tags.length > 0) {
      e.preventDefault();
      setTags(tags.slice(0, -1));
      setCopyAsEntrance(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
    setCopyAsEntrance(false);
  };

  const handleSave = () => {
    if (entryPoint) {
      onSave({
        ...entryPoint,
        name,
        photos,
        video,
        notesSelection,
        tags,
      });
    }
    onClose();
  };

  const isFormValid = () => {
    const hasPhotos = photos.filter((p) => p !== null).length === 3;
    const hasNotes = (notesSelection && Object.values(notesSelection).some((arr: Option[]) => arr && arr.length > 0)) || (tags && tags.length > 0);
    return hasPhotos && hasNotes;
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      setLocalHasError(true);
      return;
    }
    handleSave();
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleSave}
      onOpen={() => {}}
      PaperProps={{
        sx: {
          height: "90vh",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={styles.fixedHeaderContainer}>
        <Box
          sx={{
            width: "40px",
            height: "4px",
            backgroundColor: "#CBD5E1",
            borderRadius: "2px",
            margin: "0 auto 16px auto",
          }}
        />

        <Box sx={styles.header}>
          <Typography sx={styles.title}>
            {buildingName} - {isExit ? "Exit Point" : "Entry Point"}
          </Typography>
          <IconButton
            onClick={handleSave}
            sx={{
              backgroundColor: "#00000099",
              padding: "4px",
              color: "white",
              "&:hover": { backgroundColor: "#E2E8F0" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box ref={drawerContentRef} sx={styles.drawerContent}>
        {isExit && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={copyAsEntrance}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setCopyAsEntrance(isChecked);
                    if (!isChecked) {
                      setSelectedEntranceId("");
                      setPhotos([null, null, null]);
                      setVideo(null);
                      setNotesSelection({});
                      setTags([]);
                    } else if (availableEntrances.length === 1) {
                      handleEntranceSelect(availableEntrances[0].id);
                    }
                  }}
                  sx={{ color: "#9443DF", "&.Mui-checked": { color: "#9443DF" } }}
                />
              }
              label="Copy as entrance"
              sx={{ mr: 0 }}
            />
            {copyAsEntrance && availableEntrances.length > 1 && (
              <FormControl sx={{ minWidth: 150 }}>
                <Select
                  value={selectedEntranceId}
                  displayEmpty
                  onChange={(e) => handleEntranceSelect(e.target.value)}
                  renderValue={(selected) => {
                    if (!selected) {
                      return "Select Entrance";
                    }
                    const ep = availableEntrances.find((e) => e.id === selected);
                    return ep ? (ep.name || ep.type) : "Select Entrance";
                  }}
                  sx={{ borderRadius: "12px", "& fieldset": { borderColor: "#E0E0E0" }, height: "40px", fontSize: "3.53vw" }}
                  size="small"
                >
                  <MenuItem value="" disabled sx={{ display: "none" }}>Select Entrance</MenuItem>
                  {availableEntrances.map((ep) => (
                    <MenuItem key={ep.id} value={ep.id} sx={{ fontSize: "3.53vw" }}>{ep.name || ep.type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {copyAsEntrance && availableEntrances.length === 0 && (
              <Typography sx={{ color: "#666", fontSize: 12 }}>No entrances to copy.</Typography>
            )}
          </Box>
        )}
        <Typography sx={styles.sectionTitle}>
          Name
          <IconButton
            onClick={() => setNamingModalOpen(true)}
            size="small"
            sx={{ padding: 0 }}
          >
            <InfoOutlinedIcon sx={styles.infoIcon} />
          </IconButton>
        </Typography>
        <OutlinedInput
          inputRef={nameInputRef}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setCopyAsEntrance(false);
          }}
          readOnly={!isEditingName}
          fullWidth
          sx={styles.nameInput}
          endAdornment={
            <InputAdornment position="end">
              <Button sx={styles.editBtn} onClick={handleEditClick}>
                {isEditingName ? "Done" : "Edit"}
              </Button>
            </InputAdornment>
          }
        />

        <Typography sx={styles.sectionTitle}>
          Photo<span style={styles.mandatoryBadge}>*</span>
        </Typography>
        <Box sx={styles.photoGrid}>
          {[0, 1, 2].map((idx) => {
            const photo = photos[idx];
            const label = photoCaptions[idx];
            return (
              <Box key={idx} sx={{ textAlign: "center", width: "80px" }}>
                {photo ? (
                  <Box sx={styles.photoContainer}>
                    <img
                      src={
                        typeof photo === "string"
                          ? photo
                          : URL.createObjectURL(photo)
                      }
                      alt={label}
                      style={styles.photoItem}
                    />
                    {entryPoint?.photoErrors?.[idx] && (
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(211, 47, 47, 0.7)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, pointerEvents: "none" }}>
                        <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: "bold", textAlign: "center", lineHeight: 1 }}>
                          Not Approved
                        </Typography>
                      </Box>
                    )}
                    <IconButton
                      sx={styles.photoDeleteButton}
                      onClick={() => handleDeletePhoto(idx)}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: "80px",
                      height: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed",
                      borderColor: displayError && !photo ? "rgb(244, 81, 81)" : "#D9D9D9",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: displayError && !photo ? "rgb(244, 81, 81)" : "#6f2fa8",
                        backgroundColor: "rgba(111, 47, 168, 0.04)",
                      },
                    }}
                    onClick={() => {
                      setRetakeIndex(idx);
                      setPhotoModalOpen(true);
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <CameraswitchOutlined
                        sx={{ fontSize: 16, color: displayError && !photo ? "rgb(244, 81, 81)" : "#666", mb: 0.5 }}
                      />
                      <Typography sx={{ fontSize: "2.35vw", color: displayError && !photo ? "rgb(244, 81, 81)" : "#666" }}>
                        Add
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Typography sx={styles.photoLabel}>
                  {label}
                  <span style={styles.mandatoryBadge}>*</span>
                </Typography>
                {displayError && !photo && (
                  <RequiredErrorBadge sx={{ mt: 0.5, justifyContent: "center" }} />
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ marginTop: "24px", marginBottom: "24px" }}>
          <Typography sx={styles.sectionTitle}>Video (Optional)</Typography>
          {video ? (
            (() => {
              const videoSrc =
                typeof video === "string" ? video : URL.createObjectURL(video);
              return (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <Box sx={{ textAlign: "center", width: "80px" }}>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        sx={{
                          position: "relative",
                          width: "80px",
                          height: "80px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          cursor: "pointer",
                          backgroundColor: "#111",
                          border: "1px solid #e0e0e0",
                        }}
                        onClick={() => setVideoPreviewOpen(true)}
                      >
                        <video
                          src={`${videoSrc}#t=0.001`}
                          muted
                          preload="metadata"
                          playsInline
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            pointerEvents: "none",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0,0,0,0.38)",
                          }}
                        >
                          <PlayArrowIcon sx={{ color: "#fff", fontSize: 28 }} />
                        </Box>
                        {entryPoint?.videoError && (
                          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(211, 47, 47, 0.7)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, pointerEvents: "none" }}>
                            <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: "bold", textAlign: "center", lineHeight: 1 }}>
                              Not Approved
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <IconButton
                        sx={{
                          ...styles.photoDeleteButton,
                          zIndex: 2,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setVideo(null);
                          setCopyAsEntrance(false);
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Box>
                    <Typography sx={styles.photoLabel}>Video</Typography>
                  </Box>

                  <Modal
                    open={videoPreviewOpen}
                    onClose={() => setVideoPreviewOpen(false)}
                    sx={{ zIndex: 2400, backdropFilter: "blur(16px)" }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "100%",
                        outline: "none",
                      }}
                    >
                      <IconButton
                        onClick={() => setVideoPreviewOpen(false)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 12,
                          color: "#fff",
                          backgroundColor: "#00000066",
                          zIndex: 1,
                          "&:hover": { backgroundColor: "#00000099" },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <video
                        src={videoSrc}
                        autoPlay
                        controls
                        playsInline
                        style={{
                          width: "100%",
                          maxHeight: "90vh",
                          display: "block",
                          background: "#000",
                        }}
                      />
                    </Box>
                  </Modal>
                </Box>
              );
            })()
          ) : (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setVideoModalOpen(true)}
              sx={{
                color: "#9443DF",
                borderColor: "#D9D9D9",
                backgroundColor: "#FFFFFF",
                padding: "12px",
                borderRadius: "8px",
                justifyContent: "flex-start",
                textTransform: "none",
              }}
            >
              <img
                src={Icons.Video}
                alt="video"
                style={{ width: 20, height: 20, marginRight: 8 }}
              />
              Add Video
            </Button>
          )}
        </Box>

        <Typography sx={styles.sectionTitle}>
          Notes<span style={styles.mandatoryBadge}>*</span>
        </Typography>

        {entryPoint?.tagsError && (
          <Typography sx={{ color: "#d32f2f", fontSize: "12px", fontWeight: "bold", mb: "2%" }}>
            Not Approved
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            padding: "8px",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          {tags.map((tag) => (
            <Box
              key={tag}
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#9443DF",
                color: "#fff",
                borderRadius: "16px",
                padding: "4px 8px",
                fontSize: "12px",
              }}
            >
              {tag}
              <IconButton
                size="small"
                onClick={() => handleRemoveTag(tag)}
                sx={{ color: "#fff", p: 0, ml: 0.5 }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
          <TextField
            variant="standard"
            placeholder={
              newTag === "" && tags.length === 0
                ? "Type notes and press comma or enter"
                : ""
            }
            value={newTag}
            onChange={handleTagInput}
            onKeyDown={handleTagKeyDown}
            InputProps={{ disableUnderline: true }}
            sx={{ flex: 1, minWidth: "200px" }}
          />
        </Box>

        {loadingNotes ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Skeleton
              variant="rounded"
              height={38}
              sx={{ borderRadius: "8px" }}
            />
            <Skeleton
              variant="rounded"
              height={38}
              sx={{ borderRadius: "8px" }}
            />
            <Skeleton
              variant="rounded"
              height={38}
              sx={{ borderRadius: "8px" }}
            />
            <Skeleton
              variant="rounded"
              height={38}
              sx={{ borderRadius: "8px" }}
            />
            <Skeleton
              variant="rounded"
              height={38}
              sx={{ borderRadius: "8px" }}
            />
          </Box>
        ) : (
          <NotesSection
            data={isExit ? exitNotesData : entranceNotesData}
            onSelectionChange={(selection) => {
              setNotesSelection(selection);
              setCopyAsEntrance(false);
            }}
            defaultValues={notesSelection}
            hasError={displayError}
            isSubmitting={false}
            noteErrors={entryPoint?.noteErrors}
            tagsError={entryPoint?.tagsError}
          />
        )}
      </Box>

      <Box sx={styles.bottomBar}>
        <Button sx={styles.draftButton} onClick={handleSave}>
          {showUpdateButtons ? "Cancel" : "Draft"}
        </Button>
        <Button sx={styles.submitButton} onClick={handleSubmit}>
          {showUpdateButtons ? "Update" : "Submit"}
        </Button>
      </Box>

      <PhotoGuidelinesModal
        open={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        onPhotosTaken={(newPhotos) => handlePhotosTaken(newPhotos)}
        isRetakeMode={retakeIndex !== null}
        retakeIndex={retakeIndex}
        retakeCaption={retakeIndex !== null ? photoCaptions[retakeIndex] : ""}
        currentPhotos={photos.filter((p) => p !== null) as unknown as string[]}
      />

      <PhotoCaptureModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        onPhotosTaken={(_, capturedVideo) =>
          handlePhotosTaken(undefined, capturedVideo)
        }
        mode="video"
        maxPhotos={0}
      />

      <EntryPointNamingModal
        open={namingModalOpen}
        onClose={() => setNamingModalOpen(false)}
      />
    </SwipeableDrawer>
  );
}
